import {isEqual, uniq} from "lodash";
import {useCallback, useEffect, useState} from "react";
import useDebouncedEffect from "use-debounced-effect";

import type {TValue} from "components/MultiSelect/types";
import {getCompanyOrgChartFiltersDone} from "store/company/companyActions";
import {
	getCompanyEthnicDiversityDataFilter,
	getCompanyFrequentlyHiredFromDataFilter,
	getCompanyGenderDiversityDataFilter,
	getCompanyLocationReportData,
	getCompanyOrganizationalChartFilter,
	getCompanyTrendStatsFilter,
} from "store/company/companyAsyncActions";
import {companiesOrgChartFiltersSelector} from "store/company/companySelectors";

import {useAppDispatch, useAppSelector, useCompanyIdParam, usePrevious} from "../index";

import {TReportFilterParams, TTimeHiredRange} from "api/companyApi/types";
import type {TUseOrgChartFiltersReturn} from "./types";

const initialSelectedFilters = {
	regions: [],
	countries: [],
	current_text_keywords_or: [],
	current_seniority: [],
	current_specialisms: [],
	current_job_functions: [],
	time_hired_range: {from: null, to: null},
};

const useOrgChartFilters = (): TUseOrgChartFiltersReturn => {
	const dispatch = useAppDispatch();

	const companyParamId = useCompanyIdParam();

	const filtersInState = useAppSelector(companiesOrgChartFiltersSelector);

	const [selectedFilters, setSelectedFilters] =
		useState<TReportFilterParams>(initialSelectedFilters);

	const [resettingInProgress, setResettingInProgress] = useState<boolean>(false);

	const prevSelectedFilters = usePrevious(selectedFilters);

	useEffect(() => {
		if (filtersInState && Object.keys(filtersInState).length) {
			const filtersInLocalState: TReportFilterParams = {...selectedFilters};

			for (const key in {...filtersInState}) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				if (filtersInState[key] !== undefined && filtersInState[key]?.length) {
					// if (key === "current_seniority") {
					//     filtersInLocalState[key] = filtersInState[key].split(",");
					// } else {
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					filtersInLocalState[key] = filtersInState[key];
					// }
				}
			}

			setSelectedFilters({...filtersInLocalState});
			// applyFilteringInProgress(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useDebouncedEffect(
		() => {
			const prevFilterApplied =
				prevSelectedFilters &&
				(Object.values(prevSelectedFilters).some(
					(filters) => Array.isArray(filters) && filters.length,
				) ||
					prevSelectedFilters.time_hired_range?.from !== null ||
					prevSelectedFilters.time_hired_range?.to !== null);

			const filterApplied =
				Object.values(selectedFilters).some(
					(filters) => Array.isArray(filters) && !!filters.length,
				) ||
				selectedFilters.time_hired_range?.from !== null ||
				selectedFilters.time_hired_range?.to !== null;

			if (
				companyParamId &&
				!isEqual(selectedFilters, prevSelectedFilters) &&
				(filterApplied || prevFilterApplied)
			) {
				const applyOrgChartFilters = async () => {
					dispatch(getCompanyOrgChartFiltersDone(selectedFilters));

					const modifiedSelectedFilters = {...selectedFilters};

					await dispatch(
						getCompanyOrganizationalChartFilter(
							companyParamId,
							modifiedSelectedFilters,
						),
					);

					dispatch(
						getCompanyEthnicDiversityDataFilter(
							companyParamId,
							modifiedSelectedFilters,
						),
					);

					dispatch(
						getCompanyGenderDiversityDataFilter(
							companyParamId,
							modifiedSelectedFilters,
						),
					);

					dispatch(getCompanyTrendStatsFilter(companyParamId, modifiedSelectedFilters));

					dispatch(
						getCompanyFrequentlyHiredFromDataFilter(
							companyParamId,
							modifiedSelectedFilters,
						),
					);

					dispatch(getCompanyLocationReportData(companyParamId, modifiedSelectedFilters));
				};

				if (!resettingInProgress) {
					applyOrgChartFilters();
				}
			}
		},
		10,
		[companyParamId, prevSelectedFilters, selectedFilters, resettingInProgress],
	);

	const handleSelectChange: TUseOrgChartFiltersReturn["onSelectChange"] = useCallback(
		(name: string, id: TValue, checked = true) => {
			setSelectedFilters((prevSelectedFilters) => {
				// Special handling for time_hired_range
				if (name === "time_hired_range_from" || name === "time_hired_range_to") {
					const field = name === "time_hired_range_from" ? "from" : "to";
					const currentRange = prevSelectedFilters.time_hired_range || {
						from: null,
						to: null,
					};
					const newRange: TTimeHiredRange = {
						from: currentRange.from,
						to: currentRange.to,
						[field]: id as string | null,
					};

					return {
						...prevSelectedFilters,
						time_hired_range: newRange,
					};
				}

				const filterName = name as keyof TReportFilterParams;
				const currentFilterState = prevSelectedFilters[filterName] as TValue[];

				let updatedFilterValues: TValue[] = [];
				let idsArray: TValue[] = [];

				if (typeof id === "string") {
					idsArray = id.split(",").map(Number);
				} else if (Array.isArray(id)) {
					idsArray = id as TValue[];
				} else {
					idsArray = [id];
				}

				if (checked) {
					// If checked is true, proceed with adding or keeping the ids
					if (
						name === "countries" ||
						name === "current_specialisms" ||
						name === "regions"
					) {
						if (typeof id !== "number") {
							if (idsArray) {
								// Add ids that are not yet in currentFilterState
								updatedFilterValues = [
									...currentFilterState,
									...idsArray.filter(
										(id: TValue) => !currentFilterState?.includes(id),
									),
								];
							}
						}
					} else {
						// Standard logic for other filters (add or retain)
						updatedFilterValues = currentFilterState?.includes(id)
							? currentFilterState
							: [...currentFilterState, id];
					}
				} else if (
					name === "countries" ||
					name === "current_specialisms" ||
					name === "regions"
				) {
					if (typeof id !== "number") {
						if (idsArray) {
							// Remove ids from the currentFilterState
							updatedFilterValues = currentFilterState?.filter(
								(filterValue) => !idsArray.includes(Number(filterValue)),
							);
						}
					}
				} else {
					// Remove the id from the state
					updatedFilterValues = currentFilterState?.filter(
						(filterValue) => filterValue !== id,
					);
				}

				// Reset if id is empty
				if (!id || id === "") {
					updatedFilterValues = [];
				}

				return {
					...prevSelectedFilters,
					[filterName]: uniq(updatedFilterValues),
				};
			});
		},
		[],
	);

	const handleResetFilters = async () => {
		setResettingInProgress(true);
		// applyFilteringInProgress(true);
		setSelectedFilters(initialSelectedFilters);

		dispatch(getCompanyEthnicDiversityDataFilter(companyParamId));
		dispatch(getCompanyGenderDiversityDataFilter(companyParamId));
		dispatch(getCompanyTrendStatsFilter(companyParamId));
		dispatch(getCompanyOrganizationalChartFilter(companyParamId));
		dispatch(getCompanyFrequentlyHiredFromDataFilter(companyParamId));

		await dispatch(getCompanyOrgChartFiltersDone(initialSelectedFilters));

		// applyFilteringInProgress(false);
		setResettingInProgress(false);
	};

	return {
		selectedFilters,
		resetFilters: handleResetFilters,
		onSelectChange: handleSelectChange,
	};
};

export default useOrgChartFilters;
